// dashboardSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api";
import localforage from "localforage";

// configure IndexedDB store
localforage.config({
  name: "MyApp",
  storeName: "dashboard_data",
});

// Async thunk to fetch and save to IndexedDB
export const fetchDashboardData = createAsyncThunk(
  "dashboard/fetchData",
  async (requestData, { dispatch, rejectWithValue }) => {
    try {
      const apiEndpoints = [
        "section_7_dashboard",
        "behavioural_and_cultural_determinants",
        "dashboard_social_determinations",
        "health_status_of_the_population_data",
        "environmental_determinants_data",
        "health_service_determinants_data",
      ];

      for (const endpoint of apiEndpoints) {
        console.log(`🔄 Fetching API: ${endpoint} ...`);
        const start = performance.now();

        try {
          const result = await api.get_Dashboard_Section(endpoint, requestData);
          const end = performance.now();
          const seconds = ((end - start) / 1000).toFixed(2);

          console.log(`✅ ${endpoint} fetched in ${seconds}s`);

          const payload = {
            key: endpoint,
            data: result?.responsemessage || null,
          };

          // Dispatch update to Redux
          dispatch(updateDashboardData(payload));

          // Persist to IndexedDB
          await localforage.setItem(endpoint, payload.data);

          // optional delay
          await new Promise((res) => setTimeout(res, 100));
        } catch (error) {
          console.error(`❌ Error fetching ${endpoint}:`, error);
          dispatch(updateDashboardData({ key: endpoint, data: null }));
        }
      }

      return true;
    } catch (error) {
      console.error("❌ Dashboard fetch error:", error);
      return rejectWithValue(error.message);
    }
  }
);

// Load persisted data from IndexedDB
export const loadDashboardFromStorage = createAsyncThunk(
  "dashboard/loadFromStorage",
  async (_, { dispatch }) => {
    const keys = [
      "section_7_dashboard",
      "behavioural_and_cultural_determinants",
      "dashboard_social_determinations",
      "health_status_of_the_population_data",
      "environmental_determinants_data",
      "health_service_determinants_data", 
    ];

    for (const key of keys) {
      const storedData = await localforage.getItem(key);
      if (storedData) {
        dispatch(updateDashboardData({ key, data: storedData }));
      }
    }
    return true;
  }
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: {
    data: {},
    loading: false,
    error: null,
    lastFetched: null,
  },
  reducers: {
    clearDashboardData: (state) => {
      state.data = {};
      state.loading = false;
      state.error = null;
      localforage.clear();
    },
    updateDashboardData: (state, action) => {
      const { key, data } = action.payload;
      state.data[key] = data;
      state.lastFetched = Date.now();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(loadDashboardFromStorage.fulfilled, (state) => {
        state.loading = false;
      });
  },
});

export const { clearDashboardData, updateDashboardData } =
  dashboardSlice.actions;
export default dashboardSlice.reducer;
